import React from 'react';
import { Flex, Heading, IconFaceUnhappy, Paragraph, TextButton } from '@zillow/constellation';
import { useWizard, WizardSubStep } from '@zillow/zrs-rental-applications';
import { usePidRemainingAttempts, useTermSet } from '../../../contexts/IdVerificationContext';
import { RENTER_CANNOT_IDV_HELP_URL } from '../../../constants/urls';
import { IdvStep, TermSet } from '../../../../../models';

interface IncorrectAnswersProps {
  id: string;
}

function LandlordContent() {
  const pidRemainingAttempts = usePidRemainingAttempts();
  const remainingAttemptsText =
    pidRemainingAttempts === 2
      ? 'You have 2 attempts left.'
      : pidRemainingAttempts === 1
        ? 'You have 1 attempt left.'
        : '';
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <IconFaceUnhappy size="md" fontColor="blue600" />
      <Heading level={4} as="h2">
        Your identity couldn’t be verified
      </Heading>
      <Paragraph>
        Some of your answers were incorrect, so we could not verify your identity. Please try again.{' '}
        {remainingAttemptsText}
      </Paragraph>
    </Flex>
  );
}

function RenterContent() {
  const pidRemainingAttempts = usePidRemainingAttempts();
  const remainingAttemptsText =
    pidRemainingAttempts === 2
      ? 'We can try to verify your identity two more times.'
      : pidRemainingAttempts === 1
        ? 'We can try to verify your identity one more time.'
        : '';
  return (
    <Flex display="flex" flexDirection="column" gap="sm" alignItems="flex-start">
      <IconFaceUnhappy size="md" fontColor="blue600" />
      <Heading level={4} as="h2">
        Your identity couldn’t be verified
      </Heading>
      <Paragraph>We couldn’t verify your identity with the information provided.</Paragraph>
      <Paragraph>
        Please check that all information is correct and submit your identity verification again.{' '}
        {remainingAttemptsText}
      </Paragraph>
      <TextButton as="a" href={RENTER_CANNOT_IDV_HELP_URL} target="_blank">
        Learn more
      </TextButton>
    </Flex>
  );
}

export default function IncorrectAnswers({ id }: IncorrectAnswersProps) {
  const { currentSubStep, onRequestStep } = useWizard();
  const termSet = useTermSet();

  return (
    <WizardSubStep
      id={id}
      hidden={currentSubStep !== id}
      meta={{
        renderNextButton: 'Retry',
        renderBackButton: () => null,
      }}
      onRequestNext={(abortController: AbortController) => {
        abortController.abort();
        onRequestStep(IdvStep.KiqOnboardingStep);
      }}
      render={termSet === TermSet.LandlordScreening ? LandlordContent : RenterContent}
    />
  );
}
